import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HeaderCard from '../../../components/Header/HeaderCard';
import TableList from '../../../components/Tools/TableList';
import { apiService } from '../../../components/Config/api.service';
import { FiDownload } from 'react-icons/fi';
import { FaMoneyBillWave, FaMoneyCheckAlt, FaUserTag, FaBuilding, FaFileExcel } from 'react-icons/fa';
import PayslipHeader from './PayslipHeader';
import AccountingJournal from '../Compta/AccountingJournal';
import { canViewAccountingJournal } from '../../../components/Auth/permissions';

function Payslips({ employeeId, setActiveTab, historyMaxMonth }) {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState({});
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(historyMaxMonth || new Date().toISOString().slice(0, 7));
    const [totals, setTotals] = useState(null);
    const [isJournalOpen, setIsJournalOpen] = useState(false);
    const [telechargementId, setTelechargementId] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 15,
        total_pages: 1,
        total_count: 0
    });

    const downloadPayslipPDF = (payslipId) => {
        if (telechargementId) return;
        setTelechargementId(payslipId);
        apiService.getBlobFile(`/payslip/pdf/${payslipId}`)
            .then(response => {
                if (response && response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `bulletin_${payslipId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            })
            .catch(error => {
                console.error('Erreur lors du téléchargement du PDF:', error);
                alert(t('payslipsList.downloadError'));
            })
            .finally(() => setTelechargementId(null));
    };

    const columns = [
        {
            key: 'Download',
            label: ' ',
            render: (item) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        downloadPayslipPDF(item.id);
                    }}
                    disabled={telechargementId === item.id}
                    title={telechargementId === item.id ? t('payslipsList.downloading') : undefined}
                    className="text-[--color-primary] hover:text-[--color2] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {telechargementId === item.id ? (
                        <span className="loading loading-spinner loading-xs" aria-label="download-loading"></span>
                    ) : (
                        <FiDownload className="text-lg" aria-label="download" />
                    )}
                </button>
            )
        },
        { key: 'Employee', label: t('payslipsList.employee') },
        {
            key: 'State',
            label: t('payslipsList.state'),
            render: (item) => {
                let badgeColor;
                switch (item.State.toLowerCase()) {
                    case 'draft':
                        badgeColor = 'bg-yellow-100 text-yellow-800';
                        break;
                    case 'verify':
                        badgeColor = 'bg-yellow-100 text-green-800';
                        break;
                    case 'done':
                        badgeColor = 'bg-green-100 text-green-800';
                        break;
                    case 'refused':
                        badgeColor = 'bg-red-100 text-red-800';
                        break;
                    default:
                        badgeColor = 'bg-gray-100 text-gray-800';
                }

                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeColor}`}>
                        {item.State === 'draft' ? t('payslipStateBadge.draft') :
                            item.State === 'verify' ? t('payslipStateBadge.pending') :
                                item.State === 'done' ? t('payslipStateBadge.validated') :
                                    item.State === 'refused' ? t('payslipStateBadge.refused') : item.State}
                    </span>
                );
            }
        },
        {
            key: 'Period',
            label: t('payslipsList.month'),
            render: (item) => {
                const dateFrom = new Date(item.De);
                return dateFrom.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });
            }
        },
        {
            key: 'gross_salary',
            label: t('payslipsList.grossSalary'),
            render: (item) => `${Number(item.gross_salary).toLocaleString(dateLocale)} CFA`
        },
        {
            key: 'net_salary',
            label: t('payslipsList.netSalary'),
            render: (item) => `${Number(item.net_salary).toLocaleString(dateLocale)} CFA`
        },
        {
            key: 'Cotisations Salariales',
            label: t('payslipsList.employeeContrib'),
            render: (item) => `${Number(item.cotisations_salariales).toLocaleString(dateLocale)} CFA`
        },
        {
            key: 'Cotisations Patronales',
            label: t('payslipsList.employerContrib'),
            render: (item) => `${Number(item.cotisations_patronales).toLocaleString(dateLocale)} CFA`
        }
    ];

    const fetchPayslips = (page = 1, search = '', filter = '', month = '') => {
        setLoading(true);

        // Calculate offset based on page number
        const offset = (page - 1) * pagination.limit;

        // Construct query params
        const params = new URLSearchParams();
        params.append('offset', offset);
        params.append('limit', pagination.limit);
        if (search) {
            params.append('search', search);
        }
        if (filter && Object.keys(filter).length > 0) {
            params.append('filter_by', JSON.stringify(filter));
        }
        if (month) {
            params.append('month', month);
        }

        let endpoint = `/payslip?${params.toString()}`;

        apiService.getElement(endpoint)
            .then(response => {
                const payslipsWithPeriod = response.data.payslips.map(payslip => ({
                    ...payslip,
                    Period: payslip.De // La période sera formatée dans le render de la colonne
                }));
                setData(payslipsWithPeriod);
                setTotals(response.data.totals || null);

                const responseData = response.data;
                if (responseData.pagination) {
                    const currentPage = Math.floor(responseData.pagination.offset / responseData.pagination.limit) + 1;

                    setPagination({
                        ...responseData.pagination,
                        page: currentPage
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('There was an error fetching the data!', error);
                setData([]);
                setPagination({
                    page: 1,
                    limit: 5,
                    total_pages: 1,
                    total_count: 0
                });
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPayslips(pagination.page, searchTerm, filterBy, selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, pagination.page, searchTerm, filterBy, selectedMonth]);

    useEffect(() => {
        if (!historyMaxMonth) return;
        setSelectedMonth((prev) => {
            if (!prev) return historyMaxMonth;
            return prev > historyMaxMonth ? historyMaxMonth : prev;
        });
    }, [historyMaxMonth]);

    const handlePageChange = (page, search, filter) => {
        const newSearchTerm = search !== undefined ? search : searchTerm;
        const newFilterBy = filter !== undefined ? filter : filterBy;

        if (newSearchTerm !== searchTerm) {
            setSearchTerm(newSearchTerm);
        }
        if (newFilterBy !== filterBy) {
            setFilterBy(newFilterBy);
        }

        fetchPayslips(page, newSearchTerm, newFilterBy, selectedMonth);
    };

    useEffect(() => {
        apiService.getElement('/employee/departments')
            .then(res => {
                const depts = res.data.departments || [];
                setDepartmentOptions(depts.map(d => ({ value: d.id, label: d.name })));
            })
            .catch(() => {});
    }, []);

    const normalizeFilterBy = (filter = {}) => ({
        ...filter,
        filters: Array.isArray(filter?.filters) ? filter.filters : [],
    });

    const departmentSelectValue = (() => {
        const current = normalizeFilterBy(filterBy).filters.find(item => item.field === 'department');
        return current ? String(current.value || '') : '';
    })();

    const handleDepartmentFilterChange = (value) => {
        const normalized = normalizeFilterBy(filterBy);
        const filters = normalized.filters.filter(item => item.field !== 'department');

        if (value) {
            filters.push({ field: 'department', operator: '=', value: Number(value) });
        }

        const nextFilter = { ...normalized, filters };
        setPagination(prev => ({ ...prev, page: 1 }));
        handlePageChange(1, searchTerm, nextFilter);
    };

    const payslipToolbarFilters = (
        <select
            className="select select-bordered select-sm min-w-[190px] bg-white"
            value={departmentSelectValue}
            onChange={(e) => handleDepartmentFilterChange(e.target.value)}
            aria-label={t('payslipsList.filterByDepartmentAria')}
        >
            <option value="">{t('payslipsList.allDepartments')}</option>
            {departmentOptions.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );

    const goBack = () => {
        if (setActiveTab) {
            setActiveTab('Bulletin');
        }
        else {
            window.history.back();
        }
    };

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on month change
    };

    const formatCurrency = (value) => {
        return `${Number(value || 0).toLocaleString(dateLocale)} CFA`;
    };

    const getStatsIcon = (type) => {
        const iconProps = { className: "text-white", size: 24 };
        switch (type) {
            case 'gross': return <FaMoneyBillWave {...iconProps} />;
            case 'net': return <FaMoneyCheckAlt {...iconProps} />;
            case 'salarial': return <FaUserTag {...iconProps} />;
            case 'patronal': return <FaBuilding {...iconProps} />;
            default: return null;
        }
    };

    return (
        <div>
            <div className="">
                <div className="flex items-center justify-between">
                    <HeaderCard
                        title={t('payslipsList.title')}
                        text={t('payslipsList.subtitle')}
                    />
                    <div className="flex items-center gap-3">
                        <PayslipHeader 
                            selectedMonth={selectedMonth}
                            handleMonthChange={handleMonthChange}
                            isHistoryView={true}
                            onBackFromHistory={goBack}
                            monthInputMax={historyMaxMonth}
                        />
                        {canViewAccountingJournal() && (
                            <button
                                onClick={() => setIsJournalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-[--color-primary] text-white rounded-lg hover:opacity-90 transition-opacity font-medium border border-[--color-primary]"
                            >
                                <FaFileExcel size={16} />
                                {t('payslipsList.accountingJournal')}
                            </button>
                        )}
                    </div>
                </div>

                {totals && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-4">
                        {[
                            { title: t('payslipsList.totalGross'), value: formatCurrency(totals.gross_salary), type: 'gross', subtitle: t('payslipsList.totalGrossSubtitle') },
                            { title: t('payslipsList.totalNet'), value: formatCurrency(totals.net_salary), type: 'net', subtitle: t('payslipsList.totalNetSubtitle') },
                            { title: t('payslipsList.totalEmployeeContrib'), value: formatCurrency(totals.cotisations_salariales), type: 'salarial', subtitle: t('payslipsList.totalEmployeeContribSubtitle') },
                            { title: t('payslipsList.totalEmployerContrib'), value: formatCurrency(totals.cotisations_patronales), type: 'patronal', subtitle: t('payslipsList.totalEmployerContribSubtitle') }
                        ].map(({ title, value, type, subtitle }) => (
                            <div key={title} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium uppercase tracking-wide text-gray-600">
                                            {title}
                                        </p>
                                        <p className="text-xl font-bold mt-2 text-gray-800">{value}</p>
                                        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
                                    </div>
                                    <div className="bg-[--color-primary] rounded-full p-3 ml-4">
                                        {getStatsIcon(type)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <div className="loading loading-spinner text-primary"></div>
                        </div>
                    ) : (
                        <TableList
                            searchQuery={searchTerm}
                            setSearchQuery={setSearchTerm}
                            filterBy={filterBy}
                            setFilterBy={setFilterBy}
                            columns={columns}
                            data={data}
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            model={'payslip'}
                            toolbarActions={payslipToolbarFilters}
                        />
                    )}
                </div>
            </div>

            <AccountingJournal 
                isOpen={isJournalOpen}
                onClose={() => setIsJournalOpen(false)}
                selectedMonth={selectedMonth}
            />
        </div>
    );
}

export default Payslips;
